// FILE: src/services/badgeAdminService.js
//
// Data access for the admin-managed certification EMBLEM badge system
// (migration 027 tables + RPCs, migration 028 storage bucket).
//
// Two audiences:
//   • The landing page (anon) calls getCertEmblems() — a public read of which
//     emblem each cert shows, joined to the badge definition. Degrades to {} on
//     any failure so callers can fall back to the static config in badges.js.
//   • The Badge Manager admin tool calls the upload + CRUD wrappers, which hit
//     SECURITY DEFINER RPCs that verify the caller is an admin server-side.
//
// All write wrappers throw a plain Error carrying the Supabase message.
// uploadBadgeAsset throws UploadValidationError (re-exported from the question
// image uploader's contract) on client-side validation failures.

import { supabase } from './supabaseClient';

export const BADGE_ASSETS_BUCKET = 'badge-assets';

// Mirrors the bucket constraints in migration 028.
export const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
export const ALLOWED_MIME_TYPES = Object.freeze([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
]);

const MIME_TO_EXT = Object.freeze({
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
});

/** Thrown when an uploaded file fails client-side validation. */
export class UploadValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'UploadValidationError';
  }
}

function safeSegment(raw, fallback) {
  const s = String(raw ?? '').replace(/[^a-z0-9_-]/gi, '_').toLowerCase();
  return s || fallback;
}

/**
 * Uploads emblem art to the badge-assets bucket and returns its public URL +
 * storage path. Path: <safeKey>-<timestamp>.<ext> so re-uploads never collide.
 *
 * @param {File}   file
 * @param {object} [ctx]
 * @param {string} [ctx.key]  a slug for the badge (e.g. label-derived). Default 'badge'.
 * @returns {Promise<{ publicUrl: string, path: string }>}
 * @throws {UploadValidationError} on client-side validation failure
 * @throws {Error} on Supabase / network failure
 */
export async function uploadBadgeAsset(file, { key } = {}) {
  if (!file) throw new UploadValidationError('No file selected.');
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    throw new UploadValidationError(
      `Unsupported file type "${file.type || 'unknown'}". Allowed: PNG, JPG, WebP, SVG.`
    );
  }
  if (file.size > MAX_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    throw new UploadValidationError(`File is too large (${mb} MB). Maximum is 5 MB.`);
  }

  const safeKey = safeSegment(key, 'badge');
  const ext = MIME_TO_EXT[file.type] || 'bin';
  const path = `${safeKey}-${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from(BADGE_ASSETS_BUCKET)
    .upload(path, file, {
      cacheControl: '31536000', // 1 year — content-addressed by timestamp
      upsert: false,
      contentType: file.type,
    });
  if (uploadError) {
    throw new Error(`Upload failed: ${uploadError.message || 'unknown error'}`);
  }

  const { data } = supabase.storage.from(BADGE_ASSETS_BUCKET).getPublicUrl(path);
  if (!data || !data.publicUrl) {
    throw new Error('Upload succeeded but the public URL could not be resolved.');
  }
  return { publicUrl: data.publicUrl, path };
}

// ── Admin CRUD (SECURITY DEFINER RPCs verify admin via JWT) ───────────────────

/**
 * All emblem badges in the pool, newest first.
 * @returns {Promise<Array<object>>}
 */
export async function listEmblemBadges() {
  const { data, error } = await supabase
    .from('cert_emblem_badges')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(`List badges failed: ${error.message}`);
  return Array.isArray(data) ? data : [];
}

/**
 * Create a free-form emblem badge.
 * @param {{label:string, colorToken?:string, assetPath?:string, assetUrl?:string}} input
 * @returns {Promise<object>} the inserted row
 */
export async function createEmblemBadge({ label, colorToken = 'product', assetPath = null, assetUrl = null } = {}) {
  const { data, error } = await supabase.rpc('create_emblem_badge', {
    p_label: label,
    p_color_token: colorToken,
    p_asset_path: assetPath,
    p_asset_url: assetUrl,
  });
  if (error) throw new Error(`Create badge failed: ${error.message}`);
  return data;
}

/**
 * Update an emblem badge. Omitted/undefined fields are left unchanged.
 * @param {string} id
 * @param {{label?:string, colorToken?:string, assetPath?:string, assetUrl?:string, active?:boolean}} patch
 * @returns {Promise<object>} the updated row
 */
export async function updateEmblemBadge(id, patch = {}) {
  if (!id) throw new Error('updateEmblemBadge: id is required');
  const { data, error } = await supabase.rpc('update_emblem_badge', {
    p_id: id,
    p_label: patch.label ?? null,
    p_color_token: patch.colorToken ?? null,
    p_asset_path: patch.assetPath ?? null,
    p_asset_url: patch.assetUrl ?? null,
    p_active: patch.active ?? null,
  });
  if (error) throw new Error(`Update badge failed: ${error.message}`);
  return data;
}

/**
 * Delete an emblem badge. Any cert assignment pointing at it is cleared
 * (FK ON DELETE SET NULL → that cert reverts to "no emblem").
 * @param {string} id
 * @returns {Promise<object>} the deleted row snapshot
 */
export async function deleteEmblemBadge(id) {
  if (!id) throw new Error('deleteEmblemBadge: id is required');
  const { data, error } = await supabase.rpc('delete_emblem_badge', { p_id: id });
  if (error) throw new Error(`Delete badge failed: ${error.message}`);
  return data;
}

/**
 * Assign an emblem badge to a cert, or pass null/undefined for "no emblem".
 * @param {string} certId
 * @param {string|null} [badgeId]
 * @returns {Promise<object>} the assignment row
 */
export async function setCertEmblem(certId, badgeId = null) {
  if (!certId) throw new Error('setCertEmblem: certId is required');
  const { data, error } = await supabase.rpc('set_cert_emblem', {
    p_cert_id: certId,
    p_badge_id: badgeId || null,
  });
  if (error) throw new Error(`Set cert emblem failed: ${error.message}`);
  return data;
}

// ── Public read for the landing page ─────────────────────────────────────────

/**
 * Resolve the emblem badge assigned to each cert, as a render-ready map.
 * Joins cert_badge_assignment → cert_emblem_badges and shapes each badge the
 * way BadgeCallout/badgeService expect (type 'product', variant = color token,
 * imageSrc = uploaded asset URL).
 *
 * Return contract (the landing page relies on the distinction):
 *   • a MAP (object) on success — Badge Manager is authoritative. A cert
 *     present with badge_id NULL maps to `null` (explicit "no emblem"); a cert
 *     absent from the map has no assignment and shows no emblem.
 *   • `null` on failure (Supabase unreachable / query error) — the caller
 *     should fall back to the static badges.js config so the page still renders.
 *
 * Never throws.
 *
 * @returns {Promise<Object<string, (object|null)>|null>}
 */
export async function getCertEmblems() {
  try {
    const { data, error } = await supabase
      .from('cert_badge_assignment')
      .select('cert_id, badge_id, cert_emblem_badges(id, label, color_token, asset_url, active)');
    if (error || !Array.isArray(data)) return null;

    const map = {};
    for (const row of data) {
      const badge = row.cert_emblem_badges;
      if (!row.badge_id || !badge || badge.active === false) {
        map[row.cert_id] = null; // explicit "no emblem"
        continue;
      }
      map[row.cert_id] = {
        id: badge.id,
        label: badge.label,
        type: 'product',
        variant: badge.color_token || 'product',
        alt: badge.label,
        imageSrc: badge.asset_url || undefined,
      };
    }
    return map;
  } catch (_e) {
    return null;
  }
}

// ── Platform / feature badges (migration 029) ─────────────────────────────────
//
// Unlike emblems, platform badges are a single GLOBAL ordered set (the hero
// carousel) — no per-cert assignment. `sort_order` drives rotation order and
// `active` gates display.

/** Shape a platform_badges row into the object BadgeCallout expects. */
function toPlatformCalloutBadge(b) {
  return {
    id: b.id,
    label: b.label,
    type: 'platform',
    variant: b.color_token || 'platform',
    alt: b.label,
    imageSrc: b.asset_url || undefined,
  };
}

/**
 * All platform badges (active + inactive), ordered for the admin list.
 * @returns {Promise<Array<object>>}
 */
export async function listPlatformBadges() {
  const { data, error } = await supabase
    .from('platform_badges')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(`List platform badges failed: ${error.message}`);
  return Array.isArray(data) ? data : [];
}

/**
 * Create a platform badge.
 * @param {{label:string, colorToken?:string, assetPath?:string, assetUrl?:string, sortOrder?:number}} input
 * @returns {Promise<object>}
 */
export async function createPlatformBadge({
  label,
  colorToken = 'platform',
  assetPath = null,
  assetUrl = null,
  sortOrder = null,
} = {}) {
  const { data, error } = await supabase.rpc('create_platform_badge', {
    p_label: label,
    p_color_token: colorToken,
    p_asset_path: assetPath,
    p_asset_url: assetUrl,
    p_sort_order: sortOrder,
  });
  if (error) throw new Error(`Create platform badge failed: ${error.message}`);
  return data;
}

/**
 * Update a platform badge. Omitted/undefined fields are left unchanged.
 * @param {string} id
 * @param {{label?:string, colorToken?:string, assetPath?:string, assetUrl?:string, sortOrder?:number, active?:boolean}} patch
 * @returns {Promise<object>}
 */
export async function updatePlatformBadge(id, patch = {}) {
  if (!id) throw new Error('updatePlatformBadge: id is required');
  const { data, error } = await supabase.rpc('update_platform_badge', {
    p_id: id,
    p_label: patch.label ?? null,
    p_color_token: patch.colorToken ?? null,
    p_asset_path: patch.assetPath ?? null,
    p_asset_url: patch.assetUrl ?? null,
    p_sort_order: patch.sortOrder ?? null,
    p_active: patch.active ?? null,
  });
  if (error) throw new Error(`Update platform badge failed: ${error.message}`);
  return data;
}

/**
 * Delete a platform badge.
 * @param {string} id
 * @returns {Promise<object>} the deleted row snapshot
 */
export async function deletePlatformBadge(id) {
  if (!id) throw new Error('deletePlatformBadge: id is required');
  const { data, error } = await supabase.rpc('delete_platform_badge', { p_id: id });
  if (error) throw new Error(`Delete platform badge failed: ${error.message}`);
  return data;
}

/**
 * Active platform badges for the landing-page carousel, in rotation order,
 * shaped for BadgeCallout. Returns `null` on failure so PlatformBadgeRow can
 * fall back to the static badges.js set (same contract as getCertEmblems).
 * @returns {Promise<Array<object>|null>}
 */
export async function getActivePlatformBadges() {
  try {
    const { data, error } = await supabase
      .from('platform_badges')
      .select('id, label, color_token, asset_url, active, sort_order')
      .eq('active', true)
      .order('sort_order', { ascending: true });
    if (error || !Array.isArray(data)) return null;
    return data.map(toPlatformCalloutBadge);
  } catch (_e) {
    return null;
  }
}
